import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import './DownloadAppsModal.css';

import { CHROME_WEB_STORE_EXTENSION_URL } from '../data/browserExtension';

import { ModalBackdrop } from './ModalBackdrop';
import { ModalHeader } from './ModalHeader';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/8080-ai/id6761405121';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.app.ai8080&pcampaignid=web_share';
export const VSCODE_EXTENSION_URL = 'vscode:extension/8080-ai.8080-ai-auth';
export const BROWSER_EXTENSION_URL = CHROME_WEB_STORE_EXTENSION_URL;

type DownloadLink = {
  ariaLabel: string;
  className: string;
  eyebrow: string;
  href: string;
  icon: string;
  title: string;
  opensNewTab?: boolean;
};

const DOWNLOAD_LINKS: DownloadLink[] = [
  {
    ariaLabel: 'Download 8080 AI on the App Store (opens in a new tab)',
    className: 'download-apps-modal__link--apple',
    eyebrow: 'Download on the',
    href: APP_STORE_URL,
    icon: 'fa-brands fa-apple',
    title: 'App Store',
    opensNewTab: true,
  },
  {
    ariaLabel: 'Get 8080 AI on Google Play (opens in a new tab)',
    className: 'download-apps-modal__link--play',
    eyebrow: 'Get it on',
    href: PLAY_STORE_URL,
    icon: 'fa-brands fa-google-play',
    title: 'Google Play',
    opensNewTab: true,
  },
  {
    ariaLabel: 'Install the 8080 AI extension for VS Code',
    className: 'download-apps-modal__link--vscode',
    eyebrow: 'Install for',
    href: VSCODE_EXTENSION_URL,
    icon: 'fa-solid fa-code',
    title: 'VS Code extension',
  },
  {
    ariaLabel: 'Install the 8080 AI browser extension (opens in a new tab)',
    className: 'download-apps-modal__link--browser',
    eyebrow: 'Add from the Chrome Web Store',
    href: BROWSER_EXTENSION_URL,
    icon: 'fa-brands fa-chrome',
    title: 'Browser extension',
    opensNewTab: true,
  },
];

const getFocusableElements = (dialog: HTMLDivElement): HTMLElement[] => (
  Array.from(dialog.querySelectorAll<HTMLElement>([
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',')))
);

// Keyboard focus management necessarily coordinates every focus boundary in the dialog.
// eslint-disable-next-line max-statements
const trapTabFocus = (event: KeyboardEvent, dialog: HTMLDivElement) => {
  const focusableElements = getFocusableElements(dialog);

  if (focusableElements.length === 0) {
    event.preventDefault();
    dialog.focus();

    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  } else if (!dialog.contains(document.activeElement)) {
    event.preventDefault();
    firstElement.focus();
  }
};

const handleModalKeyDown = (
  event: KeyboardEvent,
  dialog: HTMLDivElement | null,
  onClose: () => void,
) => {
  if (event.key === 'Escape') {
    event.preventDefault();
    onClose();

    return;
  }

  if (event.key === 'Tab' && dialog) trapTabFocus(event, dialog);
};

const useDownloadModalKeyboard = (
  dialogRef: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  onClose: () => void,
) => {
  // Opening and closing coordinates focus, scrolling, and keyboard listeners.
  // eslint-disable-next-line max-statements
  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousBodyOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => {
      dialogRef.current?.querySelector<HTMLElement>('.auth-close-button')?.focus();
    });
    const handleKeyDown = (event: KeyboardEvent) => (
      handleModalKeyDown(event, dialogRef.current, onClose)
    );

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      previouslyFocused?.focus();
    };
  }, [dialogRef, isOpen, onClose]);
};

const DownloadLinkCard = ({ link }: { link: DownloadLink }) => (
  <a
    aria-label={link.ariaLabel}
    className={`download-apps-modal__link ${link.className}`}
    href={link.href}
    rel={link.opensNewTab ? 'noopener noreferrer' : undefined}
    target={link.opensNewTab ? '_blank' : undefined}
  >
    <span className="download-apps-modal__icon" aria-hidden="true">
      <i className={link.icon} />
    </span>
    <span className="download-apps-modal__copy">
      <small>{link.eyebrow}</small>
      <strong>{link.title}</strong>
    </span>
    <i
      aria-hidden="true"
      className={link.opensNewTab
        ? 'fa-solid fa-arrow-up-right-from-square download-apps-modal__arrow'
        : 'fa-solid fa-arrow-right download-apps-modal__arrow'}
    />
  </a>
);

type DownloadAppsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function DownloadAppsModal({ isOpen, onClose }: DownloadAppsModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useDownloadModalKeyboard(dialogRef, isOpen, onClose);

  if (!isOpen) return null;

  return createPortal(
    <ModalBackdrop
      className="download-apps-modal-backdrop"
      isVisible
      onClick={onClose}
    >
      <div
        aria-label="Download 8080 AI apps and extensions"
        aria-modal="true"
        className="auth-modal-content download-apps-modal"
        onClick={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <ModalHeader
          onClose={onClose}
          subtitle="Use 8080.AI wherever you build. Choose an app or extension to get started."
          title="Download 8080.AI"
        />
        <div className="download-apps-modal__grid">
          {DOWNLOAD_LINKS.map((link) => (
            <DownloadLinkCard key={link.href} link={link} />
          ))}
        </div>
      </div>
    </ModalBackdrop>,
    document.body,
  );
}

export function DownloadAppsControl({ className = '' }: { className?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const closeModal = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <button
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Download apps and extensions"
        className={`download-apps-control${className ? ` ${className}` : ''}`}
        onClick={() => setIsOpen(true)}
        title="Download apps and extensions"
        type="button"
      >
        <i aria-hidden="true" className="fa-solid fa-download" />
      </button>
      <DownloadAppsModal isOpen={isOpen} onClose={closeModal} />
    </>
  );
}
